import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from '@resiliatech/birdsong'
import { Box, IconButton, Tooltip, TablePagination } from '@mui/material'
import {
  StyledTableContainer,
  StyledTableCell,
  StyledTableRow,
  ActionButtons,
} from './AdminTable.styled'

export interface AdminTableAction<T = any> {
  icon: React.ReactNode
  tooltip: string
  onClick: (row: T) => void
  disabled?: (row: T) => boolean
}

export interface AdminTableColumn<T = any> {
  key: string
  label: string
  render?: (value: any, row: T) => React.ReactNode
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
}

interface AdminTableProps<T = any> {
  columns: AdminTableColumn<T>[]
  data: T[]
  loading?: boolean
  onRowClick?: (row: T) => void
  actions?: AdminTableAction<T>[]
  keyField?: string
  emptyMessage?: string
  pageSize?: number
  totalCount?: number
  currentPage?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
  serverSidePagination?: boolean
}

export const AdminTable = <T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  onRowClick,
  actions = [],
  keyField = 'id',
  emptyMessage = 'No data found',
  pageSize = 10,
  totalCount,
  currentPage = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  serverSidePagination = false,
}: AdminTableProps<T>) => {
  const [localPage, setLocalPage] = useState(0)
  const [orderBy, setOrderBy] = useState<string>('')
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')

  const page = serverSidePagination ? currentPage : localPage

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedData = React.useMemo(() => {
    if (!orderBy) return data

    return [...data].sort((a, b) => {
      const aValue = a[orderBy]
      const bValue = b[orderBy]

      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      let comparison: number
      if (aValue < bValue) {
        comparison = -1
      } else if (aValue > bValue) {
        comparison = 1
      } else {
        comparison = 0
      }
      return order === 'asc' ? comparison : -comparison
    })
  }, [data, orderBy, order])

  const paginatedData = serverSidePagination
    ? sortedData
    : sortedData.slice(page * pageSize, page * pageSize + pageSize)

  const handleChangePage = (event: unknown, newPage: number) => {
    if (serverSidePagination && onPageChange) {
      onPageChange(newPage)
    } else {
      setLocalPage(newPage)
    }
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10)
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize)
    }
    // Reset to first page when changing page size
    if (serverSidePagination && onPageChange) {
      onPageChange(0)
    } else {
      setLocalPage(0)
    }
  }

  const displayedCount =
    serverSidePagination && totalCount ? totalCount : data.length

  if (loading) {
    return (
      <Box p={4} textAlign='center'>
        Loading...
      </Box>
    )
  }

  if (data.length === 0) {
    return (
      <Box p={4} textAlign='center'>
        {emptyMessage}
      </Box>
    )
  }

  return (
    <Paper>
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <StyledTableCell
                  key={column.key}
                  align={column.align || 'left'}
                  component='th'
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.key}
                      direction={orderBy === column.key ? order : 'asc'}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </StyledTableCell>
              ))}
              {actions.length > 0 && (
                <StyledTableCell align='right' component='th'>
                  Actions
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <StyledTableRow
                key={String(row[keyField])}
                onClick={() => onRowClick?.(row)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key as string}
                    align={column.align || 'left'}
                  >
                    {column.render
                      ? column.render(row[column.key], row)
                      : row[column.key]?.toString() || '-'}
                  </TableCell>
                ))}
                {actions.length > 0 && (
                  <TableCell align='right'>
                    <ActionButtons onClick={(e) => e.stopPropagation()}>
                      {actions.map((action) => (
                        <Tooltip key={action.tooltip} title={action.tooltip}>
                          <span>
                            <IconButton
                              size='small'
                              onClick={() => action.onClick(row)}
                              disabled={action.disabled?.(row)}
                            >
                              {action.icon}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ))}
                    </ActionButtons>
                  </TableCell>
                )}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
      <TablePagination
        component='div'
        count={displayedCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={pageSize}
        rowsPerPageOptions={pageSizeOptions}
        onRowsPerPageChange={
          onPageSizeChange ? handleChangeRowsPerPage : undefined
        }
      />
    </Paper>
  )
}
